;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/app/assets/connections/:serviceSlug\",\"regex\":\"^/app/assets/connections/([^/]+)$\",\"paramNames\":[\"serviceSlug\"]},{\"path\":\"/app/assets/connections/private/:selectedApi\",\"regex\":\"^/app/assets/connections/private/([^/]+)$\",\"paramNames\":[\"selectedApi\"]},{\"path\":\"/app/assets/connections/sdk-credentials/:path*?\",\"regex\":\"^/app/assets/connections/sdk-credentials(?:/(.*))?$\",\"paramNames\":[\"path\"]},{\"path\":\"/app/assets/folders/:folder\",\"regex\":\"^/app/assets/folders/([^/]+)$\",\"paramNames\":[\"folder\"]},{\"path\":\"/app/assets/zaps/folders/:folder\",\"regex\":\"^/app/assets/zaps/folders/([^/]+)$\",\"paramNames\":[\"folder\"]}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/app/assets/agents\"},{\"path\":\"/app/assets/canvases\"},{\"path\":\"/app/assets/chatbots\"},{\"path\":\"/app/assets/connections\"},{\"path\":\"/app/assets/connections/apps\"},{\"path\":\"/app/assets/dev/connect-apps-preview\"},{\"path\":\"/app/assets/favorites\"},{\"path\":\"/app/assets/folders\"},{\"path\":\"/app/assets/forms\"},{\"path\":\"/app/assets/health\"},{\"path\":\"/app/assets/next-gen-zaps\"},{\"path\":\"/app/assets/tables\"},{\"path\":\"/app/assets/templates\"},{\"path\":\"/app/assets/zaps\"},{\"path\":\"/app/assets/zaps/approvals\"},{\"path\":\"/app/assets/zaps/folders\"}]}";import * as Sentry from "@sentry/nextjs";

const ignoreErrors = [
  // Ignore http://bit.ly/2hk42Ap, resulting from what
  // appears to be a Chrome bug http://bit.ly/2gVbncS
  /__gCrWeb\.autofill\.extractForms/,
  // Ignore http://bit.ly/2hk71Zv, resulting from
  // some really strange issue in Safari 9. Experienced
  // by Mozilla team, too http://bit.ly/2hk9vHq
  /docs-homescreen-gb-container/,
  // Ignore http://bit.ly/2mVGcgX. It's a weird react error.
  // Google says it _might_ be a logging error? http://bit.ly/2lEB3ZW
  /e\.getHostNode/,
  // Ignore http://bit.ly/2lyZSao, resulting from
  // some Chrome plugin presumably.
  /ahk_main/,
  // Some weird firefox errors that seem to happen only on firefox for iOS.
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1394296
  /__firefox__\./,
];

function isCustomExclusion(event: any) {
  let ignore = false;

  if (event.exception) {
    const values = event.exception?.values || [];
    for (const exception of values) {
      for (const error of ignoreErrors) {
        if (exception?.value?.match(error) !== null) {
          ignore = true;
          break;
        }
      }
    }
  }

  return ignore;
}

function censorSensitiveInformation(event: any) {
  const keys = [
    "password",
    "secret",
    /token$/i,
    "credentials",
    // NOTE: `email`, `username`, and `ip_address` have been added since
    // noticing these identifiers in `event.user` -- these qualify as PII
    // https://engineering.zapier.com/guides/event-streaming/privacy-by-design/#what-is-personal-data
    // https://docs.sentry.io/platforms/javascript/enriching-events/identify-user/
    "email",
    "username",
    "ip_address",
  ];

  for (const eventKey in event) {
    const removeKey = keys.reduce(
      (result, key) => result || eventKey.match(key) !== null,
      false,
    );

    if (removeKey) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete event[eventKey];
    } else if (typeof event[eventKey] === "object") {
      censorSensitiveInformation(event[eventKey]);
    }
  }
}

Sentry.init({
  // `allowUrls` is a configuration option that allows Sentry to
  // filter out errors from certain URLs. This is useful for ignoring
  // errors that are not relevant to the application, such as browser
  // extensions or third-party scripts.
  // https://docs.sentry.io/platforms/javascript/configuration/filtering-errors/#filtering-errors-based-on-url
  allowUrls:
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
      ? [/^https:\/\/(?:www\.)?zapier\.com(?:\/app\/assets)(?:\/.*)?$/]
      : [/^https:\/\/(?:www\.)?zapier-staging\.com(?:\/app\/assets)(?:\/.*)?$/],

  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_VERCEL_ENV,

  ignoreErrors: [
    // Ignore http://bit.ly/2hk42Ap, resulting from what
    // appears to be a Chrome bug http://bit.ly/2gVbncS
    /__gCrWeb\.autofill\.extractForms/,
    // Ignore http://bit.ly/2hk71Zv, resulting from
    // some really strange issue in Safari 9. Experienced
    // by Mozilla team, too http://bit.ly/2hk9vHq
    /docs-homescreen-gb-container/,
    // Ignore http://bit.ly/2mVGcgX. It's a weird react error.
    // Google says it _might_ be a logging error? http://bit.ly/2lEB3ZW
    /e\.getHostNode/,
    // Ignore http://bit.ly/2lyZSao, resulting from
    // some Chrome plugin presumably.
    /ahk_main/,
    // Some weird firefox errors that seem to happen only on firefox for iOS.
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1394296
    /__firefox__\./,
  ],

  // Associate error events with a release version, so we can track which
  // release introduced new bugs. More info on Sentry releases:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#release
  release: process.env.NEXT_PUBLIC_COMMIT_SHA,

  // for Sentry.browserTracingIntegration -- 0.2 / 20% of `production`
  // transactions, 1.0 / 100% of `development` and `preview`
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#traces-sample-rate
  tracesSampleRate:
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ? 0.2 : 1,

  integrations: [
    // Enables performance metrics such as throughput, latency, and displaying
    // the impact of errors across multiple systems (read: Zapier services)
    // https://docs.sentry.io/platforms/javascript/configuration/integrations/browsertracing/
    Sentry.browserTracingIntegration(),

    // Extracts all non-native attributes from the Error object and attaches
    // them to the event as the `extra` data.
    // https://docs.sentry.io/platforms/javascript/configuration/integrations/extraerrordata/
    Sentry.extraErrorDataIntegration({ captureErrorCause: true }),

    // NOTE: Source Maps are shared with Sentry via the Gitlab Integration
    // https://zapier-1.sentry.io/settings/integrations/gitlab/118206/

    // NOTE: Sentry's Component Tracking is enabled via the `withProfiler`
    // higher-order component in `AppLayout` -- any components *not* nested
    // within an instance of `AppLayout` will not receive this same tracing
    // treatment unless the `withProfiler` HOC is applied to the layout. This
    // SDK feature requires enabling Sentry.browserTracingIntegration.
    // https://docs.sentry.io/platforms/javascript/guides/react/features/component-monitoring/
  ],

  beforeSend(event: Sentry.ErrorEvent) {
    if (isCustomExclusion(event)) {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }

    censorSensitiveInformation(event);

    if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
      console.info(
        "Sentry would have captured client exception:",
        event.message,
      );
      // eslint-disable-next-line unicorn/no-null
      return null;
    }

    return event;
  },
});
// }

// This export will instrument router navigations, and is only relevant if you enable tracing.
// `captureRouterTransitionStart` is available from SDK version 9.12.0 onwards
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
